import React, { ReactElement, useMemo, useState } from 'react';
import { useRouter } from 'next/router';
import useSWR from 'swr';
import { Box } from '@mui/material';
import { useSnackbar } from 'notistack';
import { EventCheckInSheet } from 'components/modals/event-check-in';
import { useProfile } from 'hooks/useProfile';
import { Event, useReceiptsApi } from 'services/api';
import { canOrganizeFeedEvent } from './rail/feedEvents';
import { useSetStarredState } from 'state/starred-events/starred-events.state';
import { SectionHeader, EmptyCard, localNowIso } from '../shared';
import {
  CompactEventCard,
  CompactEventCardSkeleton,
  COMPACT_EVENT_CARD_WIDTH,
} from './CompactEventCard';

// Figma 8087:4958 — fixed 400px cards wrap; do not shrink into leftover space.
const yourEventsGridSx = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: '32px',
} as const;

const yourEventsCardSx = {
  width: `${COMPACT_EVENT_CARD_WIDTH}px`,
  maxWidth: '100%',
  flex: '0 0 auto',
} as const;

interface Reservation {
  event: Event;
  receipt: { eventId: string; receiptId: string };
  checkedIn: boolean;
}

interface ReservedResponse {
  count: number;
  reservations: Reservation[];
}

export interface YourEventsProps {}

export const YourEvents = (): ReactElement => {
  const router = useRouter();
  const { profile } = useProfile();
  const { enqueueSnackbar } = useSnackbar();
  const receiptsApi = useReceiptsApi();
  const now = useMemo(() => localNowIso(), []);
  const rReservations = useSWR<ReservedResponse>(
    `/events/reserved?timeFilter=upcoming&now=${encodeURIComponent(now)}`,
  );
  const reservations = rReservations.data?.reservations ?? [];

  useSetStarredState(reservations.map(r => r.event));

  const isLoading = !rReservations.data && rReservations.isValidating;
  const [showTicketModal, setShowTicketModal] = useState(false);
  const [ticketModalData, setTicketModalData] = useState<{
    receiptId: string;
    tickets: any[];
    event: Event;
  } | null>(null);
  const [loadingReceiptId, setLoadingReceiptId] = useState<string | null>(null);

  const handleCheckIn = async (reservation: Reservation) => {
    const { receipt, event } = reservation;
    if (loadingReceiptId) return;

    setLoadingReceiptId(receipt.receiptId);
    try {
      const result = await receiptsApi.getReceipt(receipt.receiptId);
      if (!result.success) {
        enqueueSnackbar('Failed to load ticket', { variant: 'error' });
        return;
      }

      setTicketModalData({
        receiptId: receipt.receiptId,
        tickets: result.value.tickets,
        event,
      });
      setShowTicketModal(true);
    } finally {
      setLoadingReceiptId(null);
    }
  };

  return (
    <Box width="100%">
      <SectionHeader title="Check in to your events" />
      <Box mt="16px">
        {isLoading ? (
          <Box sx={yourEventsGridSx}>
            {[0, 1, 2].map(i => (
              <Box key={i} sx={yourEventsCardSx}>
                <CompactEventCardSkeleton />
              </Box>
            ))}
          </Box>
        ) : reservations.length === 0 ? (
          <EmptyCard
            icon="event_available"
            title="Your week's wide open."
            body="Tap into events on campus — we'll save your spot."
            cta={{
              label: 'Discover events',
              onClick: () =>
                router.replace(
                  {
                    pathname: router.pathname,
                    query: { ...router.query, tab: 'events' },
                  },
                  undefined,
                  { shallow: true },
                ),
            }}
          />
        ) : (
          <Box sx={yourEventsGridSx}>
            {reservations.map(reservation => {
              const { event, receipt, checkedIn } = reservation;
              return (
                <Box key={receipt.receiptId} sx={yourEventsCardSx}>
                  <CompactEventCard
                    event={event}
                    checkedIn={checkedIn}
                    showDetails
                    ctaLoading={loadingReceiptId === receipt.receiptId}
                    checkInLabel={
                      canOrganizeFeedEvent(event, profile)
                        ? 'Check in attendees'
                        : 'Check in'
                    }
                    onCheckIn={() => handleCheckIn(reservation)}
                    onDetails={() => router.push(event.url)}
                    onEventClick={() => router.push(event.url)}
                  />
                </Box>
              );
            })}
          </Box>
        )}
      </Box>

      {ticketModalData && (
        <EventCheckInSheet
          open={showTicketModal}
          onClose={() => setShowTicketModal(false)}
          receiptId={ticketModalData.receiptId}
          tickets={ticketModalData.tickets.map(t => ({
            firstName: t.firstName,
            lastName: t.lastName,
            email: t.email,
            ticketNumber: t.ticketNumber,
            typeId: t.typeId,
            checkedInDate: t.checkInDate,
          }))}
          event={ticketModalData.event}
        />
      )}
    </Box>
  );
};
